import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import type {
  CreateCompanyPayload,
  CreatedCompany,
  CreatePerimeterJobPayload,
  LegalEntitySearchResponse,
  PerimeterJob,
} from './types';

const TERMINAL_JOB_STATUSES = new Set(['succeeded', 'failed']);

/**
 * Саджест юрлица по названию или ИНН. Запрос уходит только с третьего символа —
 * DaData тарифицирует обращения, а по одной букве полезного ответа всё равно нет.
 */
export const useLegalEntitySearchQuery = (query: string, enabled = true) => {
  const trimmed = query.trim();

  return useQuery<LegalEntitySearchResponse>({
    queryKey: ['onboarding', 'legal-entities', trimmed],
    queryFn: async () => {
      const response = await apiClient.get<LegalEntitySearchResponse>(
        '/onboarding/legal-entities',
        { params: { q: trimmed, limit: 8 } }
      );
      return response.data;
    },
    enabled: enabled && trimmed.length >= 3,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CreatedCompany, unknown, CreateCompanyPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<{ company: CreatedCompany }>(
        '/admin/companies',
        payload
      );
      return response.data.company;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'me', 'companies'] });
    },
  });
};

export const useCreatePerimeterJobMutation = () => {
  return useMutation<PerimeterJob, unknown, CreatePerimeterJobPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<PerimeterJob>('/onboarding/perimeter-jobs', payload);
      return response.data;
    },
  });
};

/** Статус сборки периметра. Поллинг прекращается сам, когда задача завершилась. */
export const usePerimeterJobQuery = (jobId: string, enabled = true) => {
  return useQuery<PerimeterJob>({
    queryKey: ['onboarding', 'perimeter-job', jobId],
    queryFn: async () => {
      const response = await apiClient.get<PerimeterJob>(
        `/onboarding/perimeter-jobs/${encodeURIComponent(jobId)}`
      );
      return response.data;
    },
    enabled: enabled && Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_JOB_STATUSES.has(status) ? false : 2000;
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });
};
